import { DateTime } from "luxon";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ExportFormat = "bai2" | "csv";

export type DatePreset = {
  label: string;
  ftlId: string;
  value: string;
  minDate: () => string;
  maxDate: () => string;
};

export const DATE_PRESETS: DatePreset[] = [
  {
    label: "Previous Month",
    ftlId: "transaction-export-date-preset-previous-month",
    value: "previous_month",
    minDate: () =>
      DateTime.now()
        .minus({ months: 1 })
        .startOf("month")
        .toFormat("yyyy-MM-dd"),
    maxDate: () =>
      DateTime.now().minus({ months: 1 }).endOf("month").toFormat("yyyy-MM-dd"),
  },
  {
    label: "Current Month",
    ftlId: "transaction-export-date-preset-current-month",
    value: "current_month",
    minDate: () => DateTime.now().startOf("month").toFormat("yyyy-MM-dd"),
    // End at today, not month-end: there are no future transactions to export,
    // and a future month-end would be rejected by the End picker's future-date
    // block (leaving the field empty).
    maxDate: () => DateTime.now().toFormat("yyyy-MM-dd"),
  },
  {
    label: "Last 30 Days",
    ftlId: "transaction-export-date-preset-last-30-days",
    value: "last_30_days",
    minDate: () => DateTime.now().minus({ days: 30 }).toFormat("yyyy-MM-dd"),
    maxDate: () => DateTime.now().toFormat("yyyy-MM-dd"),
  },
  {
    label: "Last 90 Days",
    ftlId: "transaction-export-date-preset-last-90-days",
    value: "last_90_days",
    minDate: () => DateTime.now().minus({ days: 90 }).toFormat("yyyy-MM-dd"),
    maxDate: () => DateTime.now().toFormat("yyyy-MM-dd"),
  },
  {
    label: "Year to Date",
    ftlId: "transaction-export-date-preset-ytd",
    value: "ytd",
    minDate: () => DateTime.now().startOf("year").toFormat("yyyy-MM-dd"),
    maxDate: () => DateTime.now().toFormat("yyyy-MM-dd"),
  },
  {
    label: "Previous Year",
    ftlId: "transaction-export-date-preset-previous-year",
    value: "previous_year",
    minDate: () =>
      DateTime.now().minus({ years: 1 }).startOf("year").toFormat("yyyy-MM-dd"),
    maxDate: () =>
      DateTime.now().minus({ years: 1 }).endOf("year").toFormat("yyyy-MM-dd"),
  },
];

export const DEFAULT_PRESET =
  DATE_PRESETS.find((p) => p.value === "current_month") ?? DATE_PRESETS[0];

/**
 * Predicate for flatpickr's `disable` config: returns true for any day after
 * today, so future dates can't be picked. Used to cap the End date picker.
 */
export const isAfterToday = (date: Date): boolean =>
  DateTime.fromJSDate(date) > DateTime.now().endOf("day");

/**
 * Maximum exportable date range. Must stay in sync with the API's validation
 * (public_api MultiAccountTransactionsDownloadSerializer); the client check is
 * a fast guard, the API remains the source of truth.
 */
export const MAX_DATE_RANGE_MONTHS = 18;

/** True when end is more than MAX_DATE_RANGE_MONTHS after start. */
export const isRangeTooLarge = (
  startDate: string,
  endDate: string
): boolean => {
  if (!startDate || !endDate) return false;
  return (
    DateTime.fromISO(endDate) >
    DateTime.fromISO(startDate).plus({ months: MAX_DATE_RANGE_MONTHS })
  );
};

// ---------------------------------------------------------------------------
// Export format metadata
// ---------------------------------------------------------------------------

export const FORMAT_DATE_DISPLAY = (iso: string): string =>
  DateTime.fromISO(iso).toFormat("MMM d, yyyy");

// ---------------------------------------------------------------------------
// Saved views
// ---------------------------------------------------------------------------

export const MAX_VIEW_NAME_LENGTH = 35;
